import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  ShieldCheck, ChevronRight, ChevronLeft, CheckCircle2, Lock,
  MapPin, Phone, Car, UserSquare2, Clock3, HelpCircle, Download,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Citizen MUKTI — one-question-at-a-time public form.
 * No login. Mobile first. Installable as a PWA.
 */

const STEPS = [
  { key: "concern",      label: "What concerned you?", icon: HelpCircle,
    placeholder: "e.g. young girls brought in late at night to a lodge",
    required: true, multiline: true, maxLen: 1000 },
  { key: "location",     label: "Where did you see this?", icon: MapPin,
    placeholder: "Neighbourhood, area, or landmark",
    required: false, maxLen: 160 },
  { key: "when",         label: "When did this happen?", icon: Clock3,
    placeholder: "e.g. last Friday evening, around 10 pm",
    required: false, maxLen: 120 },
  { key: "description",  label: "Anything else we should know?", icon: HelpCircle,
    placeholder: "People, cars, routines, repeated patterns",
    required: false, multiline: true, maxLen: 1500 },
  { key: "phone",        label: "A suspicious phone number? (optional)", icon: Phone,
    placeholder: "e.g. 98450 11220", required: false, maxLen: 30 },
  { key: "vehicle",      label: "A vehicle number? (optional)", icon: Car,
    placeholder: "e.g. KA 05 BL 1234", required: false, maxLen: 20 },
  { key: "alias",        label: "A name or alias used? (optional)", icon: UserSquare2,
    placeholder: "e.g. Raju, Moni, Sameer", required: false, maxLen: 80 },
  { key: "reporter_contact", label: "Your phone or email — only if you want us to contact you (optional)",
    icon: Lock, placeholder: "Kept strictly confidential",
    required: false, maxLen: 120, confidential: true },
];

function registerSW() {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker
      .register("/citizen-sw.js", { scope: "/citizen/" })
      .catch(() => { /* non-fatal */ });
  }
}

export default function CitizenReport() {
  const [idx, setIdx] = useState(0);
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { registerSW(); }, []);

  // link the citizen manifest from this page (so Add-to-Home works even if
  // we didn't modify public/index.html).
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "manifest";
    link.href = "/citizen-manifest.webmanifest";
    link.id = "citizen-manifest-link";
    document.head.appendChild(link);
    const prev = document.title;
    document.title = "Citizen MUKTI — Report a Concern";
    return () => {
      const el = document.getElementById("citizen-manifest-link");
      if (el) el.remove();
      document.title = prev;
    };
  }, []);

  const current = STEPS[idx];
  const total = STEPS.length;
  const progress = Math.round(((idx + 1) / total) * 100);

  const canNext = useMemo(() => {
    if (!current.required) return true;
    const v = (values[current.key] || "").trim();
    return v.length >= 4;
  }, [current, values]);

  const onNext = () => {
    if (idx < total - 1) setIdx(idx + 1);
  };
  const onBack = () => {
    if (idx > 0) setIdx(idx - 1);
  };

  const onSubmit = async () => {
    const concern = (values.concern || "").trim();
    if (concern.length < 4) {
      setIdx(0);
      toast.error("Please describe what concerned you.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        concern,
        location: values.location || "",
        when: values.when || "",
        description: values.description || "",
        phone: values.phone || "",
        vehicle: values.vehicle || "",
        alias: values.alias || "",
        reporter_contact: values.reporter_contact || "",
      };
      const { data } = await axios.post(`${API}/citizen/reports`, payload);
      setReceipt(data);
      toast.success(data.duplicate ? "Report already received" : "Report submitted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (receipt) {
    return <ReceiptScreen receipt={receipt} />;
  }

  const Icon = current.icon;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col"
         data-testid="citizen-report-root">
      {/* Hero header */}
      <header className="bg-gradient-to-br from-orange-600 to-blue-900 text-white px-5 py-6 sm:px-8 sm:py-8">
        <div className="max-w-xl mx-auto">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] opacity-90">
            <ShieldCheck className="h-4 w-4" />
            <span>Citizen MUKTI · Confidential</span>
          </div>
          <h1 className="font-display font-bold tracking-tight mt-3 text-3xl sm:text-4xl leading-tight">
            Report a concern.
            <br />
            Help trained investigators act.
          </h1>
          <p className="mt-3 text-sm sm:text-base opacity-90 max-w-md leading-relaxed">
            MUKTI does not identify individuals as criminals. Every report goes to trained human
            investigators who connect it with other signals.
          </p>
        </div>
      </header>

      {/* Progress strip */}
      <div className="bg-white border-b border-slate-200 px-5 py-3 sm:px-8">
        <div className="max-w-xl mx-auto flex items-center justify-between text-xs font-mono tracking-wider text-slate-500">
          <span>Step {idx + 1} of {total}</span>
          <span className="uppercase">{current.required ? "Required" : "Optional"}</span>
        </div>
        <div className="max-w-xl mx-auto h-1.5 mt-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-orange-500 transition-all"
            style={{ width: `${progress}%` }}
            data-testid="citizen-progress"
          />
        </div>
      </div>

      {/* Question card */}
      <main className="flex-1 px-5 py-8 sm:px-8">
        <div className="max-w-xl mx-auto">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center shrink-0">
              <Icon className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <label
                htmlFor="citizen-input"
                className="block text-xl sm:text-2xl font-display font-semibold leading-snug"
                data-testid="citizen-step-label"
              >
                {current.label}
              </label>
              {current.confidential && (
                <p className="mt-1 text-xs text-slate-500 flex items-center gap-1">
                  <Lock className="h-3 w-3" /> Not shown to investigators by default.
                </p>
              )}
            </div>
          </div>

          <div className="mt-5">
            {current.multiline ? (
              <Textarea
                id="citizen-input"
                data-testid={`citizen-input-${current.key}`}
                value={values[current.key] || ""}
                onChange={(e) => setValues({ ...values, [current.key]: e.target.value.slice(0, current.maxLen) })}
                placeholder={current.placeholder}
                rows={5}
                className="text-base"
                autoFocus
              />
            ) : (
              <Input
                id="citizen-input"
                data-testid={`citizen-input-${current.key}`}
                value={values[current.key] || ""}
                onChange={(e) => setValues({ ...values, [current.key]: e.target.value.slice(0, current.maxLen) })}
                placeholder={current.placeholder}
                className="text-base h-12"
                autoFocus
                inputMode={current.key === "phone" ? "tel" : "text"}
              />
            )}
            <div className="mt-1 text-right text-[11px] text-slate-400 font-mono">
              {(values[current.key] || "").length}/{current.maxLen}
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between gap-3">
            <Button
              variant="ghost"
              onClick={onBack}
              disabled={idx === 0}
              data-testid="citizen-back-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            {idx < total - 1 ? (
              <Button
                onClick={onNext}
                disabled={!canNext}
                data-testid="citizen-next-btn"
                className="bg-slate-900 hover:bg-slate-800"
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={onSubmit}
                disabled={submitting}
                data-testid="citizen-submit-btn"
                className="bg-orange-600 hover:bg-orange-700"
              >
                {submitting ? "Submitting…" : "Submit report"}
              </Button>
            )}
          </div>

          {!current.required && (
            <button
              onClick={onNext}
              className="mt-4 text-xs text-slate-500 underline underline-offset-2 hover:text-slate-700"
              data-testid="citizen-skip-btn"
            >
              Skip this question
            </button>
          )}
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-white px-5 py-5 text-xs text-slate-500">
        <div className="max-w-xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <span className="font-mono tracking-wider uppercase">Investigative use only</span>
          <Link to="/citizen/receipt" className="underline hover:text-slate-700"
                data-testid="citizen-receipt-link">
            Check an earlier report
          </Link>
        </div>
      </footer>
    </div>
  );
}

function ReceiptScreen({ receipt }) {
  const id = receipt.report_id;
  const copyId = async () => {
    try { await navigator.clipboard.writeText(id); toast.success("Copied"); } catch { toast.error("Could not copy"); }
  };
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6"
         data-testid="citizen-receipt-screen">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center">
        <div className="h-14 w-14 rounded-full bg-green-100 text-green-700 flex items-center justify-center mx-auto">
          <CheckCircle2 className="h-7 w-7" />
        </div>
        <h2 className="font-display font-bold text-2xl mt-5">
          {receipt.duplicate ? "Already received" : "Thank you"}
        </h2>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">{receipt.message}</p>
        <div className="mt-6 p-4 rounded-lg bg-slate-100 text-left">
          <div className="text-[11px] text-slate-500 font-mono uppercase tracking-wider">Report ID</div>
          <div className="mt-1 font-mono text-sm break-all" data-testid="citizen-report-id">{id}</div>
          <Button
            size="sm" variant="outline" onClick={copyId}
            className="mt-3" data-testid="citizen-copy-id-btn"
          >
            <Download className="h-3.5 w-3.5 mr-1" /> Copy ID
          </Button>
        </div>
        <Link
          to={`/citizen/receipt/${id}`}
          className="block mt-6 text-sm text-orange-700 underline underline-offset-2"
        >
          Check report status later
        </Link>
      </div>
    </div>
  );
}

export function CitizenReceipt() {
  const { id } = useParams();
  const [status, setStatus] = useState(null);
  const [lookupId, setLookupId] = useState(id || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (id) fetchStatus(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchStatus = async (reportId) => {
    setLoading(true); setError("");
    try {
      const { data } = await axios.get(`${API}/citizen/reports/${reportId}/receipt`);
      setStatus(data);
    } catch (err) {
      setError(err.response?.status === 404 ? "No report found with this ID." : "Could not look up this ID.");
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const onLookup = (e) => {
    e.preventDefault();
    if (lookupId.trim()) navigate(`/citizen/receipt/${lookupId.trim()}`);
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6 flex flex-col items-center">
      <div className="max-w-md w-full">
        <Link to="/citizen/report" className="text-xs font-mono uppercase tracking-wider text-slate-500 hover:text-slate-900">
          ← New report
        </Link>
        <h1 className="font-display font-bold text-2xl mt-4">Report status</h1>
        <form onSubmit={onLookup} className="mt-4 flex gap-2">
          <Input
            value={lookupId} onChange={(e) => setLookupId(e.target.value)}
            placeholder="Paste report ID"
            data-testid="citizen-lookup-input"
          />
          <Button type="submit" data-testid="citizen-lookup-btn">Look up</Button>
        </form>
        {loading && <div className="mt-6 text-sm text-slate-500">Loading…</div>}
        {error && <div className="mt-6 text-sm text-red-600" data-testid="citizen-lookup-error">{error}</div>}
        {status && (
          <div className="mt-6 bg-white border border-slate-200 rounded-xl p-5" data-testid="citizen-status-card">
            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500">Status</div>
            <div className="text-lg font-semibold mt-1 capitalize">{status.status}</div>
            {status.concern && <div className="mt-3 text-sm text-slate-700">{status.concern}</div>}
            {status.location && <div className="mt-1 text-xs text-slate-500">Location: {status.location}</div>}
            <div className="mt-3 text-[11px] text-slate-400 font-mono">
              Submitted {new Date(status.created_at).toLocaleString()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
